import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Search, Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const KnowledgeBase = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [glossary, setGlossary] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGlossary();
  }, []);

  const loadGlossary = async () => {
    try {
      const response = await axios.get(`${API}/glossary`);
      setGlossary(response.data);
    } catch (error) {
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', ...new Set(glossary.map(term => term.category))];

  const filteredGlossary = glossary.filter(term => {
    const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         term.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || term.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="knowledge-base">
      {/* Header */}
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                StartupBlackbox
              </span>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              data-testid="nav-dashboard-btn"
              className="text-slate-300 hover:text-cyan-400"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <img src={user.picture || 'https://via.placeholder.com/40'} alt={user.name} className="w-9 h-9 rounded-full border-2 border-cyan-500" />
              <span className="text-sm text-slate-300">{user.name}</span>
            </div>
            <Button
              variant="ghost"
              onClick={onLogout}
              data-testid="logout-btn"
              className="text-slate-400 hover:text-red-400"
              type="button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Startup Knowledge <span className="text-cyan-400">Repository</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Master the business concepts that drive startup success. Interactive glossary with definitions.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              data-testid="search-glossary-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search terms..."
              className="pl-12 bg-slate-800/50 border-slate-700 text-slate-100 py-6 text-base"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map(category => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              data-testid={`category-${category.toLowerCase()}-btn`}
              variant={selectedCategory === category ? 'default' : 'outline'}
              className={selectedCategory === category
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                : 'border-slate-700 text-slate-300 hover:border-cyan-500'}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Glossary Terms */}
        <div className="max-w-4xl mx-auto space-y-4" data-testid="glossary-list">
          {filteredGlossary.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-400">No terms found matching your search.</p>
            </div>
          ) : (
            filteredGlossary.map(term => (
              <Card
                key={term.id}
                data-testid={`glossary-term-${term.term.toLowerCase().replace(/\s+/g, '-')}`}
                className="glossary-term bg-slate-800/40 border-l-4 border-cyan-500 hover:bg-slate-800/60 p-6 backdrop-blur-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-semibold text-cyan-400">{term.term}</h3>
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                        {term.category}
                      </span>
                    </div>
                    <p className="text-slate-300 leading-relaxed">{term.definition}</p>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;